import type { Object3D } from 'three';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter.js';

/**
 * EXPERIMENTAL STL export.
 *
 * Design doc §Export: "STL ignores materials and expects watertight geometry
 * for many downstream uses. Houses generated as visually segmented wall
 * assemblies may not automatically be printable or fabrication-ready." The
 * UI must label this as experimental for that reason.
 */
export function exportToStl(
  root: Object3D,
  options: { includeSliced?: boolean } = {},
): Blob {
  const { includeSliced = false } = options;

  const visibilitySnapshot = new Map<Object3D, boolean>();
  if (!includeSliced) {
    root.traverse((obj) => {
      visibilitySnapshot.set(obj, obj.visible);
      obj.visible = true;
    });
  }

  try {
    const exporter = new STLExporter();
    const result = exporter.parse(root, { binary: true });
    // STLExporter returns DataView when binary:true and string otherwise; both
    // BlobPart-compatible once we pass their underlying buffer.
    const buf =
      typeof result === 'string'
        ? result
        : new Uint8Array(result.buffer, result.byteOffset, result.byteLength);
    return new Blob([buf], { type: 'model/stl' });
  } finally {
    if (!includeSliced) {
      for (const [obj, vis] of visibilitySnapshot) {
        obj.visible = vis;
      }
    }
  }
}
